import "./CartProducts.scss";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  IconButton,
} from "@mui/material";
import { useLanguage } from "../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";

const CartProducts = ({
  cartItems = [],
  services = [],
  utilityItems = [],
  packages = [],
  appointmentDetails,
  onRemoveItem,
  onClearCart,
}) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { hasPackages, updateServiceQuantity, updateUtilityQuantity } = useCart();

  console.log("CartProducts rendered with items:", { services, utilityItems, packages });

  // Smart navigation based on what's in the cart
  const handleEmptyCartNavigation = () => {
    if (hasPackages) {
      navigate("/packages");
    } else {
      navigate("/services");
    }
  };

  // Handle increment quantity for services
  const handleIncrement = (serviceId, currentQuantity) => {
    const newQuantity = currentQuantity + 1;
    if (newQuantity <= 99) {
      updateServiceQuantity(serviceId, newQuantity);
    }
  };

  // Handle decrement quantity for services
  const handleDecrement = (serviceId, currentQuantity) => {
    const newQuantity = currentQuantity - 1;
    if (newQuantity <= 0) {
      onRemoveItem(serviceId);
    } else {
      updateServiceQuantity(serviceId, newQuantity);
    }
  };

  // Handle increment quantity for utility items
  const handleUtilityIncrement = (itemId, currentQuantity) => {
    const newQuantity = currentQuantity + 1;
    if (newQuantity <= 99) {
      updateUtilityQuantity(itemId, newQuantity);
    }
  };

  // Handle decrement quantity for utility items
  const handleUtilityDecrement = (itemId, currentQuantity) => {
    const newQuantity = currentQuantity - 1;
    if (newQuantity <= 0) {
      onRemoveItem(itemId);
    } else {
      updateUtilityQuantity(itemId, newQuantity);
    }
  };

  // Text helper functions based on language
  const getItemsLabel = () => {
    if (language === "ar") {
      return `العناصر المختارة: ${cartItems.length}`;
    }
    return `Selected Items: ${cartItems.length}`;
  };

  const getDeleteAllLabel = () => {
    return language === "ar" ? "حذف الكل" : "Delete All";
  };

  const getEmptyCartMessage = () => {
    return language === "ar"
      ? "لم يتم اختيار أي خدمات أو اشتراكات"
      : "No services or packages selected";
  };

  const getReturnButtonLabel = () => {
    if (language === "ar") {
      return hasPackages ? "العودة للاشتراكات" : "العودة للخدمات";
    } else {
      return hasPackages ? "Return to Packages" : "Return to Services";
    }
  };

  const getDepartmentLabel = () => {
    return language === "ar" ? "القسم:" : "Department:";
  };

  const getDateLabel = () => {
    return language === "ar" ? "التاريخ:" : "Date:";
  };

  const getTimeLabel = () => {
    return language === "ar" ? "الوقت:" : "Time:";
  };

  const getServicesLabel = () => {
    return language === "ar" ? "الخدمات" : "Services";
  };

  const getUtilityItemsLabel = () => {
    return language === "ar" ? "المستلزمات" : "Utility Items";
  };

  const getPackagesLabel = () => {
    return language === "ar" ? "الاشتراكات" : "Subscription Packages";
  };

  return (
    <div className="cart-products">
      <div className="text-container">
        <div className="right">{getItemsLabel()}</div>
        <div className="left">
          {cartItems.length > 0 && (
            <p className="delete-all" onClick={onClearCart}>
              {getDeleteAllLabel()}
            </p>
          )}
        </div>
      </div>

      {/* Appointment details section - only show if there are services */}
      {services.length > 0 && appointmentDetails && (
        <div className="appointment-details">
          <div className="appointment-header">
            {language === "ar" ? "تفاصيل الموعد" : "Appointment Details"}
          </div>
          <div className="appointment-info">
            {appointmentDetails.department && (
              <div className="appointment-item">
                <StoreOutlinedIcon />
                <span className="label">{getDepartmentLabel()}</span>
                <span className="value">
                  {appointmentDetails.department.name}
                </span>
              </div>
            )}
            <div className="appointment-item">
              <CalendarMonthOutlinedIcon />
              <span className="label">{getDateLabel()}</span>
              <span className="value">{appointmentDetails.date}</span>
            </div>
            <div className="appointment-item">
              <AccessTimeOutlinedIcon />
              <span className="label">{getTimeLabel()}</span>
              <span className="value">
                {language === "ar"
                  ? `من ${appointmentDetails.time} إلى ${appointmentDetails.endTime}`
                  : `${appointmentDetails.time} to ${appointmentDetails.endTime}`}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="services-container">
        {cartItems.length > 0 ? (
          <>
            {/* Services Section */}
            {services.length > 0 && (
              <div className="services-section">
                <h3 className="section-title">{getServicesLabel()}</h3>
                {services.map((service) => (
                  <div className="service-item" key={service.id || service._id}>
                    <div className="service-info">
                      <h3 className="service-name">
                        {typeof service.name === "object"
                          ? service.name[language] ||
                            service.name.en ||
                            service.name.ar
                          : service.name}
                      </h3>
                      <div className="service-details">
                        <span className="service-price">
                          {service.price} {language === "ar" ? "ر.س" : "SAR"}
                        </span>
                        {service.quantity && service.quantity > 1 && (
                          <span className="total-price">
                            {language === "ar" ? "المجموع:" : "Total:"}{" "}
                            {service.price * service.quantity}{" "}
                            {language === "ar" ? "ر.س" : "SAR"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quantity Controls */}
                    <div className="quantity-controls">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDecrement(
                            service.id || service._id,
                            service.quantity || 1
                          );
                        }}
                        className="quantity-button"
                        disabled={!service.quantity || service.quantity <= 1}
                      >
                        <RemoveIcon className="remove-btn" />
                      </IconButton>

                      <span className="quantity-display">
                        {service.quantity || 1}
                      </span>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIncrement(
                            service.id || service._id,
                            service.quantity || 1
                          );
                        }}
                        className="quantity-button"
                        disabled={service.quantity >= 99}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </div>

                    <button
                      className="remove-btn"
                      onClick={() => onRemoveItem(service.id || service._id)}
                      title={
                        language === "ar" ? "حذف الخدمة" : "Remove Service"
                      }
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Utility Items Section */}
            {utilityItems.length > 0 && (
              <div className="utility-items-section">
                <h3 className="section-title">{getUtilityItemsLabel()}</h3>
                {utilityItems.map((item) => (
                  <div className="service-item utility-item" key={item.id || item._id}>
                    <div className="service-info">
                      <h3 className="service-name">
                        {typeof item.name === "object"
                          ? item.name[language] ||
                            item.name.en ||
                            item.name.ar
                          : item.name}
                      </h3>
                      <div className="service-details">
                        <span className="service-price">
                          {item.price} {language === "ar" ? "ر.س" : "SAR"}
                        </span>
                        {item.quantity && item.quantity > 1 && (
                          <span className="total-price">
                            {language === "ar" ? "المجموع:" : "Total:"}{" "}
                            {item.price * item.quantity}{" "}
                            {language === "ar" ? "ر.س" : "SAR"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quantity Controls */}
                    <div className="quantity-controls">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUtilityDecrement(
                            item.id || item._id,
                            item.quantity || 1
                          );
                        }}
                        className="quantity-button"
                        disabled={!item.quantity || item.quantity <= 1}
                      >
                        <RemoveIcon className="remove-btn" />
                      </IconButton>

                      <span className="quantity-display">
                        {item.quantity || 1}
                      </span>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUtilityIncrement(
                            item.id || item._id,
                            item.quantity || 1
                          );
                        }}
                        className="quantity-button"
                        disabled={item.quantity >= 99}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </div>

                    <button
                      className="remove-btn"
                      onClick={() => onRemoveItem(item.id || item._id)}
                      title={
                        language === "ar" ? "حذف المستلزم" : "Remove Item"
                      }
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Packages Section */}
            {packages.length > 0 && (
              <div className="packages-section">
                <h3 className="section-title">{getPackagesLabel()}</h3>
                {packages.map((pkg) => (
                  <div
                    className="service-item package-item"
                    key={pkg.id || pkg._id}
                  >
                    <div className="service-info">
                      <h3 className="service-name">
                        {typeof pkg.name === "object"
                          ? pkg.name[language] || pkg.name.en || pkg.name.ar
                          : pkg.name}
                      </h3>
                      <span className="service-price">
                        {pkg.price} {language === "ar" ? "ر.س" : "SAR"}
                        {pkg.period && (
                          <span className="period">
                            /
                            {language === "ar"
                              ? pkg.period === "monthly"
                                ? "شهر"
                                : "سنة"
                              : pkg.period === "monthly"
                              ? "month"
                              : "year"}
                          </span>
                        )}
                      </span>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => onRemoveItem(pkg.id || pkg._id)}
                      title={
                        language === "ar" ? "حذف الاشتراك" : "Remove Package"
                      }
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="empty-services">
            <p>{getEmptyCartMessage()}</p>
            <button className="return-btn" onClick={handleEmptyCartNavigation}>
              {getReturnButtonLabel()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartProducts;